import React, { useState, useEffect } from 'react';
import UiGiftCards from '@rcg/ui-react/lib/components/GiftCards';
import { useGlobal } from '@rcg/ui-react';
import { useLazyQuery } from '@apollo/client';

import { formatPrice } from '@rcg/pricing-format';
import { findErrorCode } from '../../commons/utils';

import { GIFT_CARD_BALANCE_INQUIRY } from '../../graphql/queries';
import {
  trackError,
  trackremoveGiftCard,
  trackSuccesGiftCard,
} from '../../commons/tracking/tracking';

const GIFTCARD_VALIDATION = 'amend:giftcardvalidation';

const GiftCards = ({
  amountValue,
  showErrorGiftCardsBanner,
  setLoader,
  setGiftCard,
  setDisableCreditCardForm,
  currentBalanceToPay,
  setCurrentBalanceToPay,
  maximumNumberOfGiftCards,
  disableGiftCards,
  affirmChecked,
}) => {
  const {
    renderContent,
    data: { localizedDayjs },
  } = useGlobal();
  const [giftCardBalancePromise, setGiftCardBalancePromise] = useState(null);
  const [giftCardAdded, setGiftCardAdded] = useState([]);
  const [updateGiftCards, setUpdateGiftCards] = useState([]);

  const dictionaries = {
    giftCardBalanceApplied: renderContent(
      'giftCardBalanceApplied',
      'Your gift card balance has been applied.',
    ),
    giftCardBalanceAppliedAddAnother: renderContent(
      'giftCardBalanceAppliedAddAnother',
      'Your gift card balance has been applied. Add another payment type to continue.',
    ),
    applyRoyalGiftCardTitle: renderContent('applyRoyalGiftCardTitle', 'Apply a Royal Gift Card'),
    applyRoyalGiftCardSubTitle: renderContent(
      'applyRoyalGiftCardSubTitle',
      'Add gift card and apply your balance to this booking',
    ),
    applyGiftCard: renderContent('applyGiftCard', 'Apply Gift Card'),
    addedGiftCardendingCard: renderContent('addedGiftCardendingCard', 'Gift card edning in'),
    addedGiftCardWillBeCharged: renderContent('addedGiftCardWillBeCharged', 'will be charged'),
    addedGiftCardRemainingBalance: renderContent(
      'addedGiftCardRemainingBalance',
      'Remaining balance',
    ),
    addedGiftCardRemoveCard: renderContent('addedGiftCardRemoveCard', 'Remove card'),
    addedGiftCardAddAnotherGiftCard: renderContent(
      'addedGiftCardAddAnotherGiftCard',
      'Add another gift card',
    ),
    giftCardInformationFormEmptyCardNumber: renderContent(
      'giftCardInformationFormEmptyCardNumber',
      'Card number is required',
    ),
    giftCardInformationFormInvalidCardNumber: renderContent(
      'giftCardInformationFormInvalidCardNumber',
      'Card number is invalid',
    ),
    giftCardInformationFormCardNumberErrorRequired: renderContent(
      'giftCardInformationFormCardNumberErrorRequired',
      'Card number is required',
    ),
    giftCardInformationFormExpireMonthErrorRequired: renderContent(
      'giftCardInformationFormExpireMonthErrorRequired',
      'Invalid date',
    ),
    giftCardInformationFormExpireYearErrorRequired: renderContent(
      'giftCardInformationFormExpireYearErrorRequired',
      'Invalid date',
    ),
    giftCardInformationFormSecurityCodeErrorRequired: renderContent(
      'giftCardInformationFormSecurityCodeErrorRequired',
      'Security code is required',
    ),
    giftCardInformationFormSecurityCodeErrorOnlyNumbers: renderContent(
      'giftCardInformationFormSecurityCodeErrorOnlyNumbers',
      'Use numbers',
    ),
    giftCardInformationFormCardNumberErrorInvalid: renderContent(
      'giftCardInformationFormCardNumberErrorInvalid',
      'The card number is invalid',
    ),
    giftCardInformationFormCreditCardAlreadyExistErrorTitle: renderContent(
      'giftCardInformationFormCreditCardAlreadyExistErrorTitle',
      'Invalid Gift Card',
    ),
    giftCardInformationFormCreditCardAlreadyExistErrorDescription: renderContent(
      'giftCardInformationFormCreditCardAlreadyExistErrorDescription',
      'You have already entered that gift card. Please use another card.',
    ),
    giftCardInformationFormCardNumberLabel: renderContent(
      'giftCardInformationFormCardNumberLabel',
      'Card number',
    ),
    giftCardInformationFormCardNumberPlaceholder: renderContent(
      'giftCardInformationFormCardNumberPlaceholder',
      'Enter your card number',
    ),
    giftCardInformationFormExpirationDateLabel: renderContent(
      'giftCardInformationFormExpirationDateLabel',
      'Expiration date',
    ),
    giftCardInformationFormSecurityCodeLabel: renderContent(
      'giftCardInformationFormSecurityCodeLabel',
      'Security code',
    ),
    giftCardInformationFormSecurityCodePlaceholder: renderContent(
      'giftCardInformationFormSecurityCodePlaceholder',
      'CVV',
    ),
    giftCardInformationFormMonthArray: localizedDayjs.localeData().monthsShort(),
    giftCardInformationFormMonthDefaultValue: renderContent(
      'bookingAmendFormReservationFieldMonthLabel',
      'Month',
    ),
    giftCardInformationFormYearDefaultValue: renderContent(
      'bookingAmendFormReservationFieldYearLabel',
      'Year',
    ),
    giftCardInformationFormSecurityCodeErrorThreeDigitsOnly: renderContent(
      'giftCardInformationFormSecurityCodeErrorThreeDigitsOnly',
      '3 digits only',
    ),
  };

  const errorHandler = (title, description) => {
    showErrorGiftCardsBanner({ description, open: true, title, type: 'error' });
  };

  const [
    executeAplyGiftCardQuery,
    {
      called: applyGiftCardCalled,
      loading: applyGiftCardLoading,
      data: applyGiftCardData,
      error: applyGiftCardError,
    },
  ] = useLazyQuery(GIFT_CARD_BALANCE_INQUIRY, { fetchPolicy: 'network-only' });

  const handleApplyGiftCardApiCall = (giftCardInfo) => {
    const { cardNumber, securityCode, expiryMonth, expiryYear } = giftCardInfo;
    const queryParams = {
      variables: {
        accountNumber: cardNumber.replace(/\s+/g, ''),
        cvv2: securityCode,
        expirationDate: `${expiryMonth < 10 ? '0' : ''}${expiryMonth}${expiryYear}`,
      },
    };
    setLoader(true);
    executeAplyGiftCardQuery(queryParams);
    return new Promise((resolve, reject) => {
      setGiftCardBalancePromise({ resolve, reject, giftCardInfo });
    });
  };

  const calculateAmounts = (balance, balanceToPay, giftcards, isAddingGiftCard) => {
    const chargedAmount = balance <= balanceToPay ? balance : balanceToPay;
    const remainingBalanceAmount =
      chargedAmount === balance ? 0 : Math.round((balance - chargedAmount) * 100) / 100;
    const balanceToPayResult =
      balanceToPay - balance <= 0 ? 0 : Math.round((balanceToPay - balance) * 100) / 100;
    const giftCardLength = isAddingGiftCard ? giftcards.length + 1 : giftcards.length;
    const addAnotherGiftCard = balanceToPayResult > 0 && giftCardLength < maximumNumberOfGiftCards;
    const roundUp = false;
    const showCurrency = true;
    const showCurrencyCode = true;
    return {
      chargedAmount,
      remainingBalanceAmount,
      balanceToPayResult,
      addAnotherGiftCard,
      roundUp,
      showCurrency,
      showCurrencyCode,
    };
  };

  const recalculateGiftCard = (
    giftCardsFiltered,
    currentBalanceToPayVar,
    forceRecalculation = false,
  ) => {
    let balanceToPayVar = currentBalanceToPayVar;
    const giftCardUiReactArray = [];
    giftCardsFiltered.forEach((giftcard) => {
      let addAnotherGiftCardVal = true;
      // eslint-disable-next-line no-param-reassign
      giftcard.addAnotherGiftCard = true;
      if (giftcard.chargedNotFormatted !== giftcard.available || forceRecalculation) {
        if (
          giftcard.chargedNotFormatted !== giftcard.available &&
          balanceToPayVar > 0 &&
          !forceRecalculation
        ) {
          balanceToPayVar += +giftcard.chargedNotFormatted;
        }
        // Recalculate charge and remaning balance
        const balance = giftcard.available;
        const { chargedAmount, remainingBalanceAmount, balanceToPayResult, addAnotherGiftCard } =
          calculateAmounts(balance, balanceToPayVar, giftCardsFiltered, false);
        balanceToPayVar = balanceToPayResult;
        addAnotherGiftCardVal = addAnotherGiftCard;

        // eslint-disable-next-line no-param-reassign
        giftcard.charged = formatPrice(+chargedAmount, giftcard.currency, true, true, true);
        // eslint-disable-next-line no-param-reassign
        giftcard.remainingBalance = formatPrice(
          +remainingBalanceAmount,
          giftcard.currency,
          true,
          true,
          true,
        );
        /* eslint-disable no-return-assign, no-param-reassign */
        giftcard.chargedNotFormatted = chargedAmount;
        giftcard.remainingBalanceNotFormatted = remainingBalanceAmount;
        giftcard.addAnotherGiftCard = addAnotherGiftCardVal;
        /* eslint-enable no-return-assign, no-param-reassign */
      }

      giftCardUiReactArray.push({
        cardNumber: giftcard.info.cardNumber,
        expiryMonth: giftcard.info.expiryMonth,
        expiryYear: giftcard.info.expiryYear,
        securityCode: giftcard.info.securityCode,
        balance: {
          charged: giftcard.charged,
          remainingBalance: giftcard.remainingBalance,
          addAnotherGiftCard: addAnotherGiftCardVal,
        },
      });
    });

    setGiftCardAdded(giftCardsFiltered);
    setGiftCard(giftCardsFiltered);
    setCurrentBalanceToPay(balanceToPayVar);
    setDisableCreditCardForm(balanceToPayVar === 0);
    return giftCardUiReactArray;
  };

  const handleRemoveGiftCard = (cardNumber) => {
    // Handle remove giftcard
    const giftCards = [...giftCardAdded];
    const giftCardsFiltered = giftCards.filter(
      (giftcard) => giftcard.info.cardNumber !== cardNumber,
    );
    const giftCardFound = giftCardAdded.find((giftcard) => giftcard.info.cardNumber === cardNumber);
    let currentBalanceToPayVar = currentBalanceToPay;
    currentBalanceToPayVar += +giftCardFound.chargedNotFormatted;
    trackremoveGiftCard();
    return recalculateGiftCard(giftCardsFiltered, currentBalanceToPayVar);
  };

  useEffect(() => {
    if (amountValue) setUpdateGiftCards(recalculateGiftCard(giftCardAdded, amountValue, true));
    // eslint-disable-next-line
  }, [amountValue, disableGiftCards]);

  useEffect(() => {
    // Handling response data
    if (applyGiftCardCalled && !applyGiftCardLoading && applyGiftCardData && !applyGiftCardError) {
      setLoader(false);
      showErrorGiftCardsBanner({ description: '', open: false, title: '', type: '' });
      const giftCardUiReactArray = [];
      const { price, status } = applyGiftCardData.giftCardBalanceInquiry;
      const { currency, value } = price;
      const giftCards = [...giftCardAdded];
      const balance = value;
      if (balance === 0 || status === 'CLOSED') {
        const title = renderContent('giftCardNoEnoughBalanceErrorTitle', 'Invalid Gift Card');
        const description = renderContent(
          'giftCardNoEnoughBalanceErrorDescription',
          'Your Gift Card doesn’t have enough balance. Please use another card.',
        );
        trackError('error', GIFTCARD_VALIDATION);
        showErrorGiftCardsBanner({ description, open: true, title, type: 'error' });
      } else {
        const { chargedAmount, remainingBalanceAmount, balanceToPayResult, addAnotherGiftCard } =
          calculateAmounts(balance, currentBalanceToPay, giftCards, true);
        const giftCardInfoNew = { ...giftCardBalancePromise.giftCardInfo };
        const giftCardBalance = {
          available: balance,
          charged: formatPrice(+chargedAmount, currency, true, true, true),
          remainingBalance: formatPrice(+remainingBalanceAmount, currency, true, true, true),
          chargedNotFormatted: chargedAmount,
          remainingBalanceNotFormatted: remainingBalanceAmount,
          addAnotherGiftCard,
          info: giftCardInfoNew,
          currency,
        };
        giftCardUiReactArray.push({
          cardNumber: giftCardInfoNew.cardNumber,
          expiryMonth: giftCardInfoNew.expiryMonth,
          expiryYear: giftCardInfoNew.expiryYear,
          securityCode: giftCardInfoNew.securityCode,
          balance: {
            charged: giftCardBalance.charged,
            remainingBalance: giftCardBalance.remainingBalance,
          },
          addAnotherGiftCard,
        });
        giftCards.push(giftCardBalance);
        setCurrentBalanceToPay(balanceToPayResult);
        setDisableCreditCardForm(balanceToPayResult === 0);
        setGiftCardAdded(giftCards);
        setGiftCard(giftCards);
        giftCardInfoNew.balance = giftCardBalance;
        giftCardBalancePromise.resolve(giftCardInfoNew);
        trackSuccesGiftCard(chargedAmount);
      }
    }
    // Handling errors
    if (applyGiftCardError) {
      setLoader(false);
      let title;
      let description;
      if (findErrorCode(applyGiftCardError, 'INCORRECT_GIFT_CARD_REQUEST_INFO')) {
        title = renderContent(
          'giftCardIncorrectGiftCardRequestInfoErrorTitle',
          'Invalid Gift Card',
        );
        description = renderContent(
          'giftCardIncorrectGiftCardRequestInfoErrorDescription',
          'There was a problem with the Gift Card you entered. Please try again or use another card.',
        );
        trackError('error', GIFTCARD_VALIDATION);
        showErrorGiftCardsBanner({ description, open: true, title, type: 'error' });
      } else if (findErrorCode(applyGiftCardError, 'GIFT_CARD_TECHNICAL_ISSUES')) {
        title = renderContent('giftCardIncorrectThechnicalIssueErrorTitle', 'Service error');
        description = renderContent(
          'giftCardIncorrectThechnicalIssueErrorDescription',
          'There was an issue in processing your request. Please try again.',
        );
        showErrorGiftCardsBanner({ description, open: true, title, type: 'error' });
        trackError('Technical issue', GIFTCARD_VALIDATION);
      } else {
        title = renderContent(
          'giftCardIncorrectThechnicalIssueNotMappedErrorTitle',
          'Service error',
        );
        description = renderContent(
          'giftCardIncorrectThechnicalIssueNotMappedErrorDescription',
          'There was an issue in processing your request. Please try again.',
        );
        trackError('error', GIFTCARD_VALIDATION);
        showErrorGiftCardsBanner({ description, open: true, title, type: 'error' });
      }
    }
    // eslint-disable-next-line
  }, [applyGiftCardCalled, applyGiftCardLoading, applyGiftCardData, applyGiftCardError]);

  return (
    <>
      <UiGiftCards
        dictionaries={dictionaries}
        handleApplyGiftCardApiCall={handleApplyGiftCardApiCall}
        handleRemoveGiftCard={handleRemoveGiftCard}
        errorHandler={errorHandler}
        updateGiftCards={updateGiftCards}
        disableApplyGiftCard={disableGiftCards}
        disabledGiftCard={affirmChecked}
      />
    </>
  );
};

export default GiftCards;
